import type { CSSProperties } from 'react';

/** A solution (product connector) the user can scope the chat to. `wrapperName`
 *  is the value sent as the request `model` to route deterministically. */
export interface SolutionDef {
  id: string;
  wrapperName: string | null;
  displayName: string;
  description?: string | null;
  icon?: string | null;
  authorized: boolean;
  connected?: boolean;
}

interface Props {
  solutions: SolutionDef[];
  /** The currently selected wrapper (from Chat's selectedWrapper). */
  activeWrapper: string;
  /** True when the chat is in General mode (no solution scope). */
  isGeneral: boolean;
  onSelectGeneral: () => void;
  onSelectSolution: (s: SolutionDef) => void;
}

/**
 * The Solution selector — a separate, entitlement-gated control that scopes the
 * chat to ONE solution (or General). Solutions the user isn't authorized for are
 * shown locked ("connect to unlock"), never silently usable. Replaces mixing
 * products into the model/persona dropdown. See docs/SOLUTION_SELECTION_PLAN.md.
 */
export function SolutionBar({ solutions, activeWrapper, isGeneral, onSelectGeneral, onSelectSolution }: Props) {
  if (!solutions || solutions.length === 0) return null;
  return (
    <div className="solution-bar" role="tablist" aria-label="Solution" style={wrap}>
      <span style={label} aria-hidden="true">Solution</span>
      <button
        type="button"
        role="tab"
        aria-selected={isGeneral}
        onClick={onSelectGeneral}
        style={{ ...chip, ...(isGeneral ? chipActive : null) }}
      >
        General
      </button>
      {solutions.map((s) => {
        const active = !isGeneral && s.wrapperName != null && s.wrapperName === activeWrapper;
        const locked = !s.authorized;
        return (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={locked}
            title={locked ? `Connect ${s.displayName} to unlock` : (s.description ?? s.displayName)}
            onClick={() => { if (!locked && s.wrapperName) onSelectSolution(s); }}
            style={{ ...chip, ...(active ? chipActive : null), ...(locked ? chipLocked : null) }}
          >
            {s.icon ? <span aria-hidden="true" style={{ marginRight: 6 }}>{s.icon}</span> : null}
            {s.displayName}
            {locked ? <span style={lockHint} aria-hidden="true"> · connect to unlock</span> : null}
          </button>
        );
      })}
    </div>
  );
}

const wrap: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  margin: '0 auto 10px',
  maxWidth: 760,
  padding: '0 2px',
};
const label: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--color-text-muted, #8a8178)',
  marginRight: 2,
};
const chip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '5px 12px',
  borderRadius: 999,
  border: '1px solid var(--color-border-default, #e0d9cd)',
  background: 'var(--color-bg-card, #fffdf8)',
  color: 'var(--color-text-secondary, #6d6358)',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
  lineHeight: 1.2,
  transition: 'border-color 0.15s, background 0.15s, color 0.15s',
};
const chipActive: CSSProperties = {
  borderColor: 'var(--brew-logo, var(--color-accent, #2c2420))',
  background: 'var(--color-bg-hover, #f1ebe1)',
  color: 'var(--color-text-primary, #241e19)',
  fontWeight: 600,
};
const chipLocked: CSSProperties = {
  cursor: 'not-allowed',
  opacity: 0.55,
};
const lockHint: CSSProperties = {
  fontSize: 11,
  opacity: 0.8,
  marginLeft: 2,
};
